import { Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SocialContentLanguageMode } from "src/api/client-api/social/useSocialContentLanguagePreference";

export type TranslatableContent = {
    content?: string;
    originalContent?: string;
    originalLocale?: string | null;
    displayedLocale?: string | null;
    translationStatus?: "original" | "translated" | "missing" | "pending";
    hasTranslation?: boolean;
    availableTranslations?: string[];
};

type SocialContentLanguageToggleProps = {
    content: TranslatableContent;
    mode: SocialContentLanguageMode;
    isUpdating?: boolean;
    onModeChange: (mode: SocialContentLanguageMode) => void;
};

export const SocialContentLanguageToggle = ({
    content,
    mode,
    isUpdating = false,
    onModeChange,
}: SocialContentLanguageToggleProps) => {
    const { t } = useTranslation();
    const hasStoredVariants = (content.availableTranslations?.length ?? 0) > 1;
    const hasDistinctPayloads =
        Boolean(content.originalContent) && Boolean(content.content) && content.originalContent !== content.content;
    const hasAlternative =
        content.translationStatus === "translated" ||
        content.translationStatus === "missing" ||
        hasStoredVariants ||
        hasDistinctPayloads;

    if (!content.originalLocale || !hasAlternative) {
        return null;
    }

    const nextMode = mode === "translated" ? "original" : "translated";
    const buttonLabel =
        mode === "translated" ?
            t("social.translation.show_original", "Show original")
        :   t("social.translation.show_translation", "Show translation");

    return (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
            {content.translationStatus === "missing" && mode === "translated" && (
                <Typography variant="caption" color="text.secondary">
                    {t("social.translation.pending", "Translation pending")}
                </Typography>
            )}
            <Button
                size="small"
                variant="text"
                disabled={isUpdating}
                onClick={() => {
                    onModeChange(nextMode);
                }}
                sx={{ minWidth: "auto", p: 0, textTransform: "none" }}
            >
                {buttonLabel}
            </Button>
        </Stack>
    );
};
